import { Check, Info, Mail, Pause, Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { Note, SectionHead } from '@/components/redesign/bits'
import { NativeSelect } from '@/components/ui/native-select'
import { SearchField } from '@/components/ui/search-field'
import { Segmented } from '@/components/ui/segmented'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState, ErrorState } from '@/components/ui/states'
import { PageTitle } from '@/app/layout'
import { errorMessage } from '@/lib/api/errors'
import { cn } from '@/lib/cn'
import { formatDateTime, numberFmt } from '@/lib/format'
import {
  type EmailList,
  type EmailSend,
  type SendStatusFilter,
  useEmailSends,
  useEmails,
  useSaveEmail,
  waitLabel,
} from './api'

const SEND = {
  sent: { label: 'Sent', tone: 'ok' },
  scheduled: { label: 'Waiting', tone: 'warn' },
  skipped: { label: 'Skipped', tone: '' },
  failed: { label: 'Failed', tone: 'bad' },
} as const satisfies Record<EmailSend['status'], { label: string; tone: string }>

const sendStatuses: { value: SendStatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'sent', label: 'Sent' },
  { value: 'scheduled', label: 'Waiting' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'failed', label: 'Failed' },
]

/** The URL's filters, with an updater that leaves the other filters alone. */
function useFilters() {
  const [params, setParams] = useSearchParams()
  const update = (next: Record<string, string>, resetPage = false) =>
    setParams(
      (p) => {
        const merged = new URLSearchParams(p)
        for (const [k, v] of Object.entries(next)) {
          if (v === '') merged.delete(k)
          else merged.set(k, v)
        }
        if (resetPage) merged.delete('page')
        return merged
      },
      { replace: true },
    )
  return { params, update }
}

/**
 * Lifecycle emails (redesign of 2026-10-16): the journey, each email in the order people meet
 * it with what it sent in the last 30 days and a switch, then every send below.
 */
export function EmailsPage() {
  const emails = useEmails()
  const canEdit = emails.data?.meta.can_edit ?? false

  return (
    <>
      <PageTitle
        title="Lifecycle emails"
        description="The emails Arva SEO sends by itself as people get started. Each goes once per person when its trigger happens, after its wait, if its condition still holds. Account emails (verification, password resets, invitations, billing) always send and aren’t listed here."
        actions={
          canEdit && (
            <Link to="/emails/new" className="btn primary">
              <Plus aria-hidden="true" />
              New email
            </Link>
          )
        }
      />

      <section className="sec">
        <SectionHead title="The journey" extra={<p>Last 30 days</p>} />
        {emails.isPending ? (
          <Skeleton className="h-40 rounded-[14px]" />
        ) : emails.isError ? (
          <div className="card">
            <ErrorState error={emails.error} onRetry={() => void emails.refetch()} />
          </div>
        ) : emails.data.data.length === 0 ? (
          <div className="card">
            <EmptyState
              icon={<Mail />}
              title="No emails yet"
              description="Add a welcome email or a getting-started tip."
            />
          </div>
        ) : (
          <>
            <Journey list={emails.data} canEdit={canEdit} />
            <Note calm icon={<Info aria-hidden="true" />} title="Then took the step">
              Of the people each email went to in the last 30 days, how many then took the step it’s there to
              prompt (added a website, verified one, connected Google data or bought a plan). It shows what
              happened after the email, not that the email caused it. Internal accounts are left out.
            </Note>
          </>
        )}
      </section>

      <Sends emails={emails.data?.data ?? []} />
    </>
  )
}

/**
 * The journey as columns, one per wait ("Straight away", "After 1 day"…), shortest first, so
 * each step shows once; the emails on that step stack in it, in the order people meet them.
 */
function Journey({ list, canEdit }: { list: EmailList; canEdit: boolean }) {
  const order = Object.keys(list.meta.triggers)
  const rank = (t: string) => (order.includes(t) ? order.indexOf(t) : order.length)
  const columns = new Map<number, EmailList['data']>()
  for (const e of [...list.data].sort(
    (a, b) => a.delay_minutes - b.delay_minutes || rank(a.trigger) - rank(b.trigger),
  )) {
    columns.set(e.delay_minutes, [...(columns.get(e.delay_minutes) ?? []), e])
  }

  return (
    <ol className="journey" aria-label="Lifecycle emails">
      {[...columns].map(([delay, emails]) => {
        const when = waitLabel(delay)
        const on = emails.some((e) => e.enabled)
        return (
          <li className="jn" key={delay} aria-label={when}>
            <span className={cn('jn-dot', !on && 'off')} aria-hidden="true">
              {on ? <Check /> : <Pause />}
            </span>
            <div className="jn-when">{when}</div>
            <ul className="jn-cards" aria-label={`Emails ${when.toLowerCase()}`}>
              {emails.map((e) => (
                <EmailCard key={e.id} e={e} list={list} canEdit={canEdit} />
              ))}
            </ul>
          </li>
        )
      })}
    </ol>
  )
}

function EmailCard({
  e,
  list,
  canEdit,
}: {
  e: EmailList['data'][number]
  list: EmailList
  canEdit: boolean
}) {
  const n = e.last_30_days
  return (
    <li className="card jn-card">
      <Link to={`/emails/${e.id}`} style={{ color: 'inherit' }}>
        <b>{e.name}</b>
      </Link>
      <small>{list.meta.triggers[e.trigger]?.label ?? e.trigger}</small>
      <small>Only if: {e.condition ? (list.meta.conditions[e.condition] ?? e.condition) : 'always'}</small>
      <div className="jn-stats">
        <span>
          <b>{numberFmt.format(n.sent)}</b> sent
        </span>
        {n.scheduled > 0 && (
          <span>
            <b>{numberFmt.format(n.scheduled)}</b> waiting
          </span>
        )}
        {n.skipped > 0 && (
          <span>
            <b>{numberFmt.format(n.skipped)}</b> skipped
          </span>
        )}
        {n.failed > 0 && (
          <span style={{ color: 'var(--bad)' }}>
            <b>{numberFmt.format(n.failed)}</b> failed
          </span>
        )}
      </div>
      {e.next_step && (
        <small>
          {e.next_step.sent === 0 ? (
            <>Then {e.next_step.label}: no sends yet</>
          ) : (
            <>
              <b>{Math.round((e.next_step.did / e.next_step.sent) * 100)}%</b> then {e.next_step.label} (
              {numberFmt.format(e.next_step.did)} of {numberFmt.format(e.next_step.sent)})
            </>
          )}
        </small>
      )}
      <div className="bar" style={{ marginTop: 10 }}>
        {e.enabled ? <span className="chip ok">On</span> : <span className="chip">Paused</span>}
        {canEdit && <Toggle id={e.id} name={e.name} enabled={e.enabled} />}
      </div>
    </li>
  )
}

/** Every send of every lifecycle email, newest first, filtered by status, email and recipient. */
function Sends({ emails }: { emails: EmailList['data'] }) {
  const { params, update } = useFilters()
  const status = (params.get('sends') as SendStatusFilter | null) ?? 'all'
  const email = params.get('email') ?? ''
  const to = params.get('to') ?? ''
  const page = Number(params.get('page') ?? 1)
  const sends = useEmailSends(status, email, to, page)
  const filtering = status !== 'all' || email !== '' || to !== ''

  const [draft, setDraft] = useState(to)
  useEffect(() => {
    const t = setTimeout(() => {
      if (draft.trim() !== to) update({ to: draft.trim() }, true)
    }, 300)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft])

  return (
    <section className="sec">
      <SectionHead
        title="Sends"
        extra={
          <div className="bar">
            <Segmented
              label="Send status"
              value={status}
              options={sendStatuses}
              onChange={(v) => update({ sends: v === 'all' ? '' : v }, true)}
            />
            <NativeSelect
              aria-label="Email"
              value={email}
              onChange={(e) => update({ email: e.target.value }, true)}
              className="h-9 w-52 text-[13px]"
            >
              <option value="">Every email</option>
              {emails.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </NativeSelect>
            <SearchField
              label="Search sends"
              placeholder="Recipient’s email"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
            />
          </div>
        }
      />

      {sends.isPending ? (
        <Skeleton className="h-40 rounded-[14px]" />
      ) : sends.isError ? (
        <div className="card">
          <ErrorState error={sends.error} onRetry={() => void sends.refetch()} />
        </div>
      ) : sends.data.data.length === 0 ? (
        <div className="card">
          <EmptyState
            title={filtering ? 'No sends match these filters' : 'Nothing sent yet'}
            description={
              filtering
                ? 'Try another status or email, or clear the search.'
                : 'Sends appear here when a trigger happens.'
            }
            action={
              filtering && (
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    setDraft('')
                    update({ sends: '', email: '', to: '' }, true)
                  }}
                >
                  Clear filters
                </button>
              )
            }
          />
        </div>
      ) : (
        <div className="tw">
          <table className="t" style={{ minWidth: 640 }}>
            <caption className="sr-only">Lifecycle email sends</caption>
            <thead>
              <tr>
                <th>Email</th>
                <th>To</th>
                <th>Status</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {sends.data.data.map((s) => (
                <tr key={s.id}>
                  <td>
                    <b>{s.email ?? 'Deleted email'}</b>
                  </td>
                  <td>{s.to ?? <span className="m">—</span>}</td>
                  <td>
                    <span className={cn('chip', SEND[s.status].tone)}>{SEND[s.status].label}</span>
                    {s.reason && <small style={{ whiteSpace: 'normal' }}>{s.reason}</small>}
                  </td>
                  <td className="m" style={{ whiteSpace: 'nowrap' }}>
                    {s.sent_at ? `Sent ${formatDateTime(s.sent_at)}` : `Due ${formatDateTime(s.send_at)}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="t-foot">
            <span>
              {sends.data.meta.last_page > 1
                ? `Page ${sends.data.meta.current_page} of ${sends.data.meta.last_page} · `
                : ''}
              {numberFmt.format(sends.data.meta.total)} {sends.data.meta.total === 1 ? 'send' : 'sends'}
            </span>
            {sends.data.meta.last_page > 1 && (
              <span className="acts">
                <button
                  type="button"
                  className="btn sm ghost"
                  disabled={page <= 1}
                  onClick={() => update({ page: String(page - 1) })}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="btn sm ghost"
                  disabled={page >= sends.data.meta.last_page}
                  onClick={() => update({ page: String(page + 1) })}
                >
                  Next
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </section>
  )
}

function Toggle({ id, name, enabled }: { id: string; name: string; enabled: boolean }) {
  const save = useSaveEmail(id)
  return (
    <button
      type="button"
      role="switch"
      className="switch push"
      aria-checked={enabled}
      aria-label={enabled ? `Turn off ${name}` : `Turn on ${name}`}
      disabled={save.isPending}
      onClick={() =>
        save.mutate(
          { enabled: !enabled },
          {
            onSuccess: (e) => toast.success(e.enabled ? `${e.name} is on.` : `${e.name} is off.`),
            onError: (err) => toast.error(errorMessage(err)),
          },
        )
      }
    />
  )
}
